(function () {
  // ─── Variant lookup ────────────────────────────────────────────────────────

  function getCurrentVariantFromVariantSelects(variantSelects) {
    if (!variantSelects) return null;

    // Old theme: VariantSelects sets currentVariant directly
    if (variantSelects.currentVariant) return variantSelects.currentVariant;

    const variantJson = variantSelects.querySelector('[type="application/json"]');
    if (!variantJson) return null;

    let variantData;
    try {
      variantData = JSON.parse(variantJson.textContent);
    } catch (e) {
      return null;
    }

    // Sense 15.4.1: data-selected-variant contains a single variant object (not array)
    if (!Array.isArray(variantData)) return variantData || null;

    // Old theme fallback: full variants array — find by current radio/select state
    const options = Array.from(variantSelects.querySelectorAll('select, fieldset'), (element) => {
      if (element.tagName === 'SELECT') return element.value;
      if (element.tagName === 'FIELDSET') {
        return Array.from(element.querySelectorAll('input')).find((radio) => radio.checked)?.value;
      }
      return null;
    });

    return variantData.find((variant) => {
      return !variant.options
        .map((option, index) => options[index] === option)
        .includes(false);
    });
  }

  // ─── DOM helpers ───────────────────────────────────────────────────────────

  function getGallery(sectionId) {
    return document.getElementById(`MediaGallery-${sectionId}`);
  }

  function getViewer(sectionId) {
    return document.getElementById(`GalleryViewer-${sectionId}`);
  }

  function getThumbnailSlider(sectionId) {
    return document.getElementById(`GalleryThumbnails-${sectionId}`);
  }

  function getVisibleItems(items) {
    return Array.from(items).filter((item) => !item.hasAttribute('hidden'));
  }

  function updateSliderCounter(sectionId, visibleSlidesLength) {
    const gallery = getGallery(sectionId);
    if (!gallery) return;
    const current = gallery.querySelector('.slider-counter--current');
    const total = gallery.querySelector('.slider-counter--total');
    if (current) current.textContent = visibleSlidesLength > 0 ? '1' : '0';
    if (total) total.textContent = String(visibleSlidesLength);
  }

  function setItemVisibility(item, visible) {
    if (!item) return;
    item.hidden = !visible;
    item.style.display = visible ? '' : 'none';
  }

  // ─── Gallery fade helpers ──────────────────────────────────────────────────

  function fadeGalleryOut(gallery) {
    if (!gallery) return;
    gallery.style.transition = 'opacity 0.12s ease';
    gallery.style.opacity = '0';
    gallery.style.pointerEvents = 'none';
  }

  function fadeGalleryIn(gallery) {
    if (!gallery) return;
    // rAF ensures DOM grouping changes are painted before we fade in
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        gallery.style.transition = 'opacity 0.22s ease';
        gallery.style.opacity = '1';
        gallery.style.pointerEvents = '';
      });
    });
  }

  // ─── Core grouping logic ───────────────────────────────────────────────────

  function applyVariantMediaGrouping(sectionId, variant) {
    const gallery = getGallery(sectionId);
    if (!gallery) return;

    const allSlides = gallery.querySelectorAll('#Slider-Gallery-' + sectionId + ' > li[data-media-id]');
    const allThumbs = gallery.querySelectorAll('#Slider-Thumbnails-' + sectionId + ' > li.thumbnail-list__item');

    if (!allSlides.length) return;

    const featuredMediaId = variant?.featured_media?.id ? String(variant.featured_media.id) : null;

    if (!featuredMediaId) {
      allSlides.forEach((item) => setItemVisibility(item, true));
      allThumbs.forEach((item) => setItemVisibility(item, true));
      getViewer(sectionId)?.resetPages?.();
      getThumbnailSlider(sectionId)?.resetPages?.();
      updateSliderCounter(sectionId, allSlides.length);
      fadeGalleryIn(gallery);
      return;
    }

    const matchingSlides = Array.from(allSlides).filter(
      (item) => item.dataset.variantGroup === featuredMediaId
    );

    const useGrouping = matchingSlides.length > 0;

    if (useGrouping) {
      allSlides.forEach((item) => {
        setItemVisibility(item, item.dataset.variantGroup === featuredMediaId);
        item.classList.remove('is-active');
      });
      allThumbs.forEach((item) => {
        setItemVisibility(item, item.dataset.variantGroup === featuredMediaId);
        const button = item.querySelector('button.thumbnail');
        if (button) button.removeAttribute('aria-current');
      });
    } else {
      allSlides.forEach((item) => {
        setItemVisibility(item, true);
        item.classList.remove('is-active');
      });
      allThumbs.forEach((item) => {
        setItemVisibility(item, true);
        const button = item.querySelector('button.thumbnail');
        if (button) button.removeAttribute('aria-current');
      });
    }

    const visibleSlides = getVisibleItems(allSlides);
    const visibleThumbs = getVisibleItems(allThumbs);

    const targetMediaKey = `${sectionId}-${featuredMediaId}`;
    const activeSlide =
      visibleSlides.find((item) => item.dataset.mediaId === targetMediaKey) || visibleSlides[0];
    if (activeSlide) activeSlide.classList.add('is-active');

    const activeThumb =
      visibleThumbs.find((item) => item.dataset.target === targetMediaKey) || visibleThumbs[0];
    if (activeThumb) {
      const button = activeThumb.querySelector('button.thumbnail');
      if (button) button.setAttribute('aria-current', 'true');
    }

    getViewer(sectionId)?.resetPages?.();
    getThumbnailSlider(sectionId)?.resetPages?.();
    updateSliderCounter(sectionId, visibleSlides.length);
    gallery.setActiveMedia?.(targetMediaKey);

    // Fade in after all DOM changes are applied
    fadeGalleryIn(gallery);
  }

  // ─── Init (page load + Customizer reload) ─────────────────────────────────

  function initVariantMediaGrouping() {
    document.querySelectorAll('variant-selects').forEach((variantSelects) => {
      const sectionId = variantSelects.dataset.section;
      const variant = getCurrentVariantFromVariantSelects(variantSelects);
      applyVariantMediaGrouping(sectionId, variant);
    });
  }

  document.addEventListener('DOMContentLoaded', initVariantMediaGrouping);
  document.addEventListener('shopify:section:load', initVariantMediaGrouping);

  // ─── PubSub: respond to swatch clicks ─────────────────────────────────────
  // PUB_SUB_EVENTS is a const in Sense 15.4.1 — NOT on window — use typeof check.

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {

    // 1. Immediate fade-out as soon as a swatch is clicked (before fetch)
    subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
      const variantSelects = data?.event?.target?.closest('variant-selects');
      if (!variantSelects) return;
      fadeGalleryOut(getGallery(variantSelects.dataset.section));
    });

    // 2. Apply grouping + fade-in after product-info.js fetch resolves
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (!data?.sectionId || !data?.variant) return;
      // setTimeout 0 ensures Sense's own media update runs first
      window.setTimeout(() => {
        applyVariantMediaGrouping(data.sectionId, data.variant);
      }, 0);
    });
  }

  // ─── Hover preload for custom swatch images ───────────────────────────────
  // Starts fetching variant images before the user clicks, reducing perceived delay.

  function initSwatchHoverPreload() {
    document.querySelectorAll('.product-form__swatch input[type="radio"][data-image-url]').forEach((input) => {
      input.addEventListener('mouseover', () => {
        const url = input.dataset.imageUrl;
        if (!url || document.querySelector(`link[rel="preload"][href="${url}"]`)) return;
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = url;
        document.head.appendChild(link);
      }, { passive: true });
    });
  }

  document.addEventListener('DOMContentLoaded', initSwatchHoverPreload);

  window.applyVariantMediaGrouping = applyVariantMediaGrouping;
})();
